import React, { useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";

// Icons
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ExploreIcon from "@mui/icons-material/Explore";
import HotelIcon from "@mui/icons-material/Hotel";
import FlightIcon from "@mui/icons-material/Flight";
import TrainIcon from "@mui/icons-material/Train";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

emailjs.init("uafuRHsbBmMGhy5xY");

const BookingForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // States for form inputs
  const [dateTime, setDateTime] = useState(dayjs());
  const [serviceType, setServiceType] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [tourType, setTourType] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  // Service type icons mapping
  const serviceIcons = {
    "Hire Vehicle": <DirectionsCarIcon />,
    "Hire a Guide": <PersonIcon />,
    "Currency Exchange": <CurrencyExchangeIcon />,
    "Book a Tour": <ExploreIcon />,
    "Book Hotel": <HotelIcon />,
    "Book Flight Ticket": <FlightIcon />,
    "Book Train Ticket": <TrainIcon />,
    "Book Bus Ticket": <DirectionsBusIcon />
  };

  // Steps for the booking process
  const steps = [
    'Select Service',
    'Enter Details',
    'Confirm Booking'
  ];

  const handleServiceChange = (event) => {
    setServiceType(event.target.value);
    // Reset dependent fields
    if (event.target.value !== "Hire Vehicle") {
      setVehicleType("");
    }
    if (event.target.value !== "Book a Tour") {
      setTourType("");
    }
  };

  // Validate step 1
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!serviceType) {
      newErrors.serviceType = "Please select a service";
    }
    
    if (serviceType === "Hire Vehicle" && !vehicleType) {
      newErrors.vehicleType = "Please select a vehicle type";
    }
    
    if (serviceType === "Book a Tour" && !tourType) {
      newErrors.tourType = "Please select a tour type";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate step 2
  const validateStep2 = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(mobile.replace(/\D/g, ''))) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (activeStep === 0) {
      if (validateStep1()) {
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      if (validateStep2()) {
        setActiveStep(2);
      }
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Prepare the template parameters
      const templateParams = {
        serviceType,
        vehicleType: serviceType === "Hire Vehicle" ? vehicleType : "",
        tourType: serviceType === "Book a Tour" ? tourType : "",
        name,
        email: email.toLowerCase(),
        mobile,
        dateTime: dateTime.toISOString(),
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        "service_xdwayot", // Your Service ID
        "template_4gpnkr8", // Your Template ID
        templateParams
      );

      // Check the response status
      if (response.status === 200) {
        alert("Booking confirmed!");
        window.location.href = "/booking-confirmation"; // Redirect to confirmation page
      } else {
        throw new Error("Failed to confirm booking.");
      }
    } catch (error) {
      console.error("Error during booking:", error);
      alert(`Error occurred while booking: ${error.message}`);
    }
  };

  // Get form content based on active step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            {/* Select Service */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.serviceType}>
                <InputLabel>Select Service</InputLabel>
                <Select
                  value={serviceType}
                  onChange={handleServiceChange}
                  startAdornment={serviceType ? null : null}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.serviceType ? "error.main" : "rgba(0, 0, 0, 0.23)"
                    }
                  }}
                >
                  <MenuItem value="Hire Vehicle">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <DirectionsCarIcon sx={{ mr: 1, color: "#db352a" }} />
                      Hire Vehicle
                    </Box>
                  </MenuItem>
                  <MenuItem value="Hire a Guide">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PersonIcon sx={{ mr: 1, color: "#db352a" }} />
                      Hire a Guide
                    </Box>
                  </MenuItem>
                  <MenuItem value="Currency Exchange">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CurrencyExchangeIcon sx={{ mr: 1, color: "#db352a" }} />
                      Currency Exchange
                    </Box>
                  </MenuItem>
                  <MenuItem value="Book a Tour">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ExploreIcon sx={{ mr: 1, color: "#db352a" }} />
                      Book a Tour
                    </Box>
                  </MenuItem>
                  <MenuItem value="Book Hotel">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <HotelIcon sx={{ mr: 1, color: "#db352a" }} />
                      Book Hotel
                    </Box>
                  </MenuItem>
                  <MenuItem value="Book Flight Ticket">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FlightIcon sx={{ mr: 1, color: "#db352a" }} />
                      Book Flight Tickets
                    </Box>
                  </MenuItem>
                  <MenuItem value="Book Train Ticket">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TrainIcon sx={{ mr: 1, color: "#db352a" }} />
                      Book Train Ticket
                    </Box>
                  </MenuItem>
                  <MenuItem value="Book Bus Ticket">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <DirectionsBusIcon sx={{ mr: 1, color: "#db352a" }} />
                      Book Bus Ticket
                    </Box>
                  </MenuItem>
                </Select>
                {errors.serviceType && <FormHelperText>{errors.serviceType}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Select Vehicle or Tour */}
            {serviceType === "Hire Vehicle" && (
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.vehicleType}>
                  <InputLabel>Select Vehicle</InputLabel>
                  <Select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.vehicleType ? "error.main" : "rgba(0, 0, 0, 0.23)"
                      }
                    }}
                  >
                    <MenuItem value="Car">Car</MenuItem>
                    <MenuItem value="Van">Tempo Traveler</MenuItem>
                    <MenuItem value="Bus">Bus</MenuItem>
                  </Select>
                  {errors.vehicleType && <FormHelperText>{errors.vehicleType}</FormHelperText>}
                </FormControl>
              </Grid>
            )}
            
            {serviceType === "Book a Tour" && (
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.tourType}>
                  <InputLabel>Select Tour</InputLabel>
                  <Select
                    value={tourType}
                    onChange={(e) => setTourType(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: errors.tourType ? "error.main" : "rgba(0, 0, 0, 0.23)"
                      }
                    }}
                  >
                    <MenuItem value="Ellora and City Tour">
                      Ellora and City Tour
                    </MenuItem>
                    <MenuItem value="South India Tour">South India Tour</MenuItem>
                    <MenuItem value="Shirdi / Shani Shingnapur Tour">
                      Shirdi / Shani Shingnapur Tour
                    </MenuItem>
                  </Select>
                  {errors.tourType && <FormHelperText>{errors.tourType}</FormHelperText>}
                </FormControl>
              </Grid>
            )}

            {/* Select Date and Time */}
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Select Date and Time"
                  value={dateTime}
                  onChange={setDateTime}
                  renderInput={(params) => 
                    <TextField 
                      {...params} 
                      fullWidth 
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <DateRangeIcon sx={{ mr: 1, color: "#db352a" }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  }
                  minDate={dayjs()}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            {/* Enter Name */}
            <Grid item xs={12}>
              <TextField
                label="Full Name"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: <PersonOutlineIcon sx={{ mr: 1, color: "#db352a" }} />
                }}
              />
            </Grid>

            {/* Enter Email */}
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: "#db352a" }} />
                }}
              />
            </Grid>

            {/* Enter Mobile Number */}
            <Grid item xs={12}>
              <TextField
                label="Mobile Number"
                placeholder="Enter your mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                fullWidth
                error={!!errors.mobile}
                helperText={errors.mobile}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: "#db352a" }} />
                }}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Card
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid rgba(0,0,0,0.08)",
                backgroundColor: "rgba(219,53,42,0.02)",
                mb: 4
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: "#db352a", fontWeight: "bold" }}>
                    Booking Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                    Service:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {serviceType}
                  </Typography>
                </Grid>
                
                {vehicleType && (
                  <>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                        Vehicle Type:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "left" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {vehicleType}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {tourType && (
                  <>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                        Tour:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "left" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {tourType}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                    Date & Time:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {dateTime.format('MMM D, YYYY h:mm A')}
                  </Typography>
                </Grid>
                
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                    Name:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {name}
                  </Typography>
                </Grid>
                
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                    Email:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {email}
                  </Typography>
                </Grid>
                
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium", color: "#555" }}>
                    Mobile:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {mobile}
                  </Typography>
                </Grid>
              </Grid>
            </Card>
            
            <Typography variant="body2" sx={{ mb: 3, color: "#555" }}>
              Please review your booking details above. Click "Confirm Booking" to complete your reservation.
            </Typography>
          </Box>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      style={{ width: "100%" }}
    >
      <Box
        sx={{
          width: { xs: "95%", md: "800px" },
          mx: "auto",
          my: 4,
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pt: 4,
            pb: 6,
            px: { xs: 2, md: 5 },
            borderRadius: "20px",
            boxShadow: "0 15px 50px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.05)",
            background: "white",
          }}
        >
          {/* Top background decoration */}
          <Box 
            sx={{ 
              position: "absolute", 
              top: 0, 
              left: 0, 
              right: 0, 
              height: "8px", 
              background: "linear-gradient(90deg, #db352a, #ff7366)" 
            }} 
          />
          
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{
              color: "#db352a",
              fontWeight: "bold",
              mb: 4
            }}
          >
            Book Your Service
          </Typography>
          
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel
            sx={{ mb: 5 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box sx={{ p: { xs: 1, md: 3 } }}>
            {getStepContent(activeStep)}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, px: { xs: 1, md: 3 } }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ 
                borderRadius: "30px",
                px: 3,
                visibility: activeStep === 0 ? 'hidden' : 'visible'
              }}
            >
              Back
            </Button>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: "30px",
                    px: 4,
                    py: 1.2,
                    backgroundColor: "#db352a",
                    fontWeight: "medium",
                    boxShadow: "0 4px 12px rgba(219,53,42,0.3)",
                    "&:hover": {
                      backgroundColor: "#c62f27",
                    }
                  }}
                  startIcon={<CheckCircleIcon />}
                >
                  Confirm Booking
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    borderRadius: "30px",
                    px: 3,
                    py: 1.2,
                    backgroundColor: "#db352a",
                    fontWeight: "medium",
                    boxShadow: "0 4px 12px rgba(219,53,42,0.3)",
                    "&:hover": {
                      backgroundColor: "#c62f27",
                    }
                  }}
                >
                  Continue
                </Button>
              )}
            </motion.div>
          </Box>
        </Paper>
      </Box>
    </motion.div>
  );
};

export default BookingForm;